import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  DEFAULT_SUGGESTIONS,
  getEnvironment,
  ToolId,
  toolsList,
} from "@packages/utils";
import {
  LessonHookCreateRequestDtoCourseName,
  LessonHookCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@packages/contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { Button } from "@core/button";
import { SuccessNotification } from "./SuccessNotification";
import { useAppSelector, useAppDispatch } from "@packages/store/hooks";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { trackEvent } from "@packages/hooks/useAnalytics";

const NOTIFICATION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
const GLOBAL_NOTIFICATION_KEY = "global_notification_dismissed_at";

const GenericToolCreationPage = ({
  useCreateTool,
  useGetTool,
  getTool,
  ToolCard,
  CreateModalWrapper,
  toolDataExtractor,
  toolType,
}: {
  useCreateTool: any;
  useGetTool: any;
  getTool: any;
  ToolCard: any;
  CreateModalWrapper: any;
  toolDataExtractor: any;
  toolType: ToolId;
}) => {
  const { mutateAsync: createTool, data } = useCreateTool();
  const [toolLoading, setIsToolLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastSuccessfulInput, setLastSuccessfulInput] = useState<any>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const notificationDismissedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const STORAGE_KEYS = useMemo(
    () => ({
      suggestedTools: "suggested_tools",
      totalCount: "total_suggestions_count",
      currentFlowCount: "current_flow_count",
    }),
    [],
  );
  const { trackEvent: track } = trackEvent();

  const updateLocalStorage = useCallback(
    (tool: string) => {
      const storageKey = `suggested_tools`;
      const currentFlowKey = "current_flow_count";

      if (!localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, JSON.stringify([toolType]));
      }
      if (!localStorage.getItem(currentFlowKey)) {
        localStorage.setItem(currentFlowKey, "0");
      }

      const flowCount = parseInt(localStorage.getItem(currentFlowKey) || "0");

      if (flowCount >= 2) {
        return false;
      }

      const suggestedTools = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.suggestedTools) ||
          JSON.stringify([toolType]),
      );

      const updatedSuggestedTools = [
        ...new Set([...suggestedTools, tool, toolType]),
      ].filter(Boolean);

      localStorage.setItem(
        STORAGE_KEYS.suggestedTools,
        JSON.stringify(updatedSuggestedTools),
      );

      return true;
    },
    [toolType, STORAGE_KEYS],
  );

  const isNotificationTimeoutActive = useCallback(() => {
    const lastDismissalTimeStr = localStorage.getItem(GLOBAL_NOTIFICATION_KEY);
    if (!lastDismissalTimeStr) return false;

    const lastDismissalTime = parseInt(lastDismissalTimeStr, 10);
    const currentTime = Date.now();
    return currentTime - lastDismissalTime < NOTIFICATION_TIMEOUT_MS;
  }, []);

  const selectedComplementaryTool = useMemo(() => {
    const storageKey = `suggested_tools`;
    const currentFlowKey = "current_flow_count";

    if (!localStorage.getItem(storageKey)) {
      localStorage.setItem(storageKey, JSON.stringify([toolType]));
    }
    if (!localStorage.getItem(currentFlowKey)) {
      localStorage.setItem(currentFlowKey, "0");
    }

    const flowCount = parseInt(localStorage.getItem(currentFlowKey) || "0");

    if (flowCount >= 2) {
      return null;
    }

    const suggestedToolFromTheList = toolsList
      .flatMap((tool) => tool.tools)
      .find((tool) => tool.id === toolType)?.suggestedTools;
    const suggestions =
      suggestedToolFromTheList?.length && suggestedToolFromTheList.length > 0
        ? suggestedToolFromTheList
        : DEFAULT_SUGGESTIONS;

    const suggestedTools = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.suggestedTools) || "[]",
    );

    const availableSuggestions = suggestions.filter(
      (tool) => !suggestedTools.includes(tool.name),
    );

    if (availableSuggestions.length === 0) {
      return null;
    }

    const selectedTool =
      availableSuggestions[
        Math.floor(Math.random() * availableSuggestions.length)
      ];

    return selectedTool;
  }, [toolType, STORAGE_KEYS]);

  useEffect(() => {
    if (!location.state?.source) {
      localStorage.setItem(STORAGE_KEYS.currentFlowCount, "0");
    }
  }, [location.state, STORAGE_KEYS, toolType]);

  const handleComplementaryToolNavigation = useCallback(
    async (input: any) => {
      if (!selectedComplementaryTool) return;

      try {
        setShowSuccessNotification(false);

        const navigationData = {
          ...input,
          fromTool: toolType,
          timestamp: new Date().toISOString(),
          isCreatedFromSuggestion: true,
        };

        const updated = updateLocalStorage(selectedComplementaryTool.name);
        if (!updated) return;

        const flowCount = parseInt(
          localStorage.getItem(STORAGE_KEYS.currentFlowCount) || "0",
        );

        navigate(selectedComplementaryTool.route, {
          state: {
            input: navigationData,
            source: toolType,
            flowCount: flowCount + 1,
            autoSubmit: true,
          },
        });
      } catch (error) {
        addToast({
          title: t("tools.complementaryTool.navigateError"),
          description: t("tools.complementaryTool.navigateErrorDesc"),
          variant: "error",
          duration: 5000,
        });
      }
    },
    [
      toolType,
      navigate,
      addToast,
      t,
      selectedComplementaryTool,
      updateLocalStorage,
      STORAGE_KEYS,
    ],
  );

  useEffect(() => {
    if (location.state?.flowCount) {
      localStorage.setItem(
        STORAGE_KEYS.currentFlowCount,
        location.state.flowCount.toString(),
      );
    }
  }, [location.state, STORAGE_KEYS]);

  const handleSubmit = useCallback(
    async (input: any) => {
      try {
        setIsToolLoading(true);
        setIsImageLoading(true);
        setError(null);
        dispatch(updateLastUsedTools(toolType));
        if (!localStorage.getItem(STORAGE_KEYS.suggestedTools)) {
          localStorage.setItem(STORAGE_KEYS.suggestedTools, JSON.stringify([]));
        }

        const suggestedTools = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.suggestedTools) || "[]",
        );

        const updatedSuggestedTools = [
          ...new Set([...suggestedTools, toolType]),
        ].filter(Boolean);

        localStorage.setItem(
          STORAGE_KEYS.suggestedTools,
          JSON.stringify(updatedSuggestedTools),
        );

        await createTool({ data: input });
        setLastSuccessfulInput(input);
      } catch (error: any) {
        const isInappropriateContent =
          error.response?.data.message ===
          "Content contains inappropriate language";

        addToast({
          title: t(
            isInappropriateContent
              ? "common.inappropriateContent.title"
              : "common.error",
          ),
          description: t(
            isInappropriateContent
              ? "common.inappropriateContent.description"
              : "common.errorDescription",
          ),
          variant: "error",
          duration: 3000,
        });

        if (!isInappropriateContent) {
          setError(
            error instanceof Error ? error : new Error("An error occurred"),
          );
          console.error("Error creating tool:", error);
        }

        setIsToolLoading(false);
        setIsImageLoading(false);
      }
    },
    [createTool, addToast, t, STORAGE_KEYS, toolType],
  );

  const initialInput = useMemo(
    () => ({
      courseName: "HISTORY" as LessonHookCreateRequestDtoCourseName,
      gradeLevel: 8,
      prompt: "",
      environment: getEnvironment(),
      isPublic: true,
      generationType:
        i18n.language === "tr" ? "MAARIF_LEARNING_OUTCOME" : "PROMPT",
    }),
    [],
  );

  useEffect(() => {
    const state = location.state as {
      input?: any;
      autoSubmit?: boolean;
    } | null;
    if (state?.autoSubmit && state.input) {
      handleSubmit(state.input);
    }
  }, [handleSubmit]);

  const { data: toolData } = useGetTool(data?.id!, {
    query: {
      enabled: data?.id !== undefined,
      refetchInterval: (data: any) => {
        if (data.state.data?.status === "FAILED") {
          setError(new Error("An error occurred"));
          return false;
        }
        const toolData = toolDataExtractor(data.state.data);
        if (!toolData) return 5000;
        if (!data?.state?.data?.imageUrl) return 3000;
        return false;
      },
      queryFn: async () => {
        const response = await getTool(data?.id!);
        const hasToolData = toolDataExtractor(response);
        const hasImage = response?.imageUrl;

        const totalCount = response.totalToolSuggestion;

        setIsToolLoading(!hasToolData);
        setIsImageLoading(hasToolData && !hasImage);

        // Tool suggestion notification feature disabled
        // if (hasToolData && hasImage) {
        //   const flowCount = parseInt(
        //     localStorage.getItem(STORAGE_KEYS.currentFlowCount) || "0",
        //   );

        //   if (
        //     !notificationDismissedRef.current &&
        //     flowCount < 2 &&
        //     totalCount < 3 &&
        //     !isNotificationTimeoutActive() &&
        //     (!lastSuccessfulInput.teacherDocumentIds ||
        //       lastSuccessfulInput.teacherDocumentIds.length === 0)
        //   ) {
        //     setShowSuccessNotification(true);
        //   }
        // }

        return response;
      },
    },
  });

  useEffect(() => {
    if (toolData && !!toolDataExtractor(toolData)) {
      track(
        {
          category: "ToolCreation",
          action: `${toolType} Generate`,
          label: `${toolType} Generation Done`,
          nonInteraction: false,
        },
        {
          location: `/${toolType}/generate`,
          element: "generate-button",
        },
      );
    }
  }, [toolData, toolDataExtractor]);

  const handleNotificationClose = useCallback(() => {
    setShowSuccessNotification(false);
    notificationDismissedRef.current = true;
    localStorage.setItem(GLOBAL_NOTIFICATION_KEY, Date.now().toString());
  }, []);

  if (!toolDataExtractor(toolData)) {
    return (
      <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          <CreateModalWrapper
            initialInput={initialInput}
            onSubmit={handleSubmit}
            isLoading={toolLoading}
            error={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <Button
          onClick={() => navigate(-1)}
          variant="solid"
          size="md"
          textColor="text-white dark:text-white"
          bgColor="bg-gray-400 dark:bg-gray-700"
          rounded="lg"
          className="mb-2"
        >
          <ArrowLeft size={24} />
        </Button>

        <div className="w-full h-0 mb-8 sm:mb-0" />
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-200 dark:border-gray-700">
          <ToolCard
            toolData={{
              ...toolData,
              gradeLevel: toolData.gradeLevel === -1 ? 0 : toolData.gradeLevel,
            }}
            onCreateAgain={() => {
              notificationDismissedRef.current = false;
              handleSubmit(lastSuccessfulInput);
            }}
            isImageLoading={isImageLoading}
          />
        </div>

        {showSuccessNotification && selectedComplementaryTool && (
          <SuccessNotification
            title={t("tools.complementaryTool.title")}
            description={t("tools.complementaryTool.description", {
              tool: t(`quickActions.${selectedComplementaryTool.name}`),
            })}
            actionLabel={t("tools.complementaryTool.action")}
            onAction={() =>
              handleComplementaryToolNavigation(lastSuccessfulInput)
            }
            onClose={handleNotificationClose}
          />
        )}
      </div>
    </div>
  );
};

export default GenericToolCreationPage;
